import { ApiError, createHttpClient, encodeUtf8HeaderValue } from '@wanjianshi/shared-http';
import { createUuid } from '@wanjianshi/shared-utils';
import { adminEnvironment } from '@/config/environment';

const CSRF_COOKIE = 'wjs_admin_csrf';

function readCookie(name: string): string {
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie.split('; ').find(value => value.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : '';
}

let unauthorizedHandler: (() => void) | undefined;
let unauthorizedRecoveryHandler: (() => Promise<boolean>) | undefined;
let forbiddenHandler: ((message: string) => void) | undefined;

export function configureAdminHttpHandlers(handlers: {
  recoverUnauthorized?: () => Promise<boolean>;
  onUnauthorized?: () => void;
  onForbidden?: (message: string) => void;
}) {
  unauthorizedRecoveryHandler = handlers.recoverUnauthorized;
  unauthorizedHandler = handlers.onUnauthorized;
  forbiddenHandler = handlers.onForbidden;
}

export const adminApi = createHttpClient({
  baseUrl: adminEnvironment.apiBaseUrl,
  timeoutMs: adminEnvironment.requestTimeoutMs,
  auth: {
    apply(init) {
      const headers = new Headers(init.headers);
      const method = String(init.method ?? 'GET').toUpperCase();
      if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
        const csrfToken = readCookie(CSRF_COOKIE);
        if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
      }
      return { ...init, headers, credentials: 'include' };
    },
    recoverUnauthorized() {
      return unauthorizedRecoveryHandler?.() ?? Promise.resolve(false);
    },
    onUnauthorized() {
      unauthorizedHandler?.();
    },
    onForbidden({ error }) {
      forbiddenHandler?.(error.message);
    },
  },
});

/**
 * 401 已由统一认证处理器接管：清理后台身份并由路由守卫跳转登录页。
 * 页面层应直接结束当前操作，避免再显示“未登录”等重复错误。
 */
export function isAdminUnauthorizedError(reason: unknown): reason is ApiError {
  return reason instanceof ApiError && reason.status === 401;
}

export function createIdempotencyKey(prefix = 'admin'): string {
  return `${prefix}-${createUuid()}`;
}

export function adminOperationOptions(
  reason?: string,
  idempotencyKey?: string,
): { headers: Headers } {
  const headers = new Headers();
  if (reason?.trim()) {
    headers.set('X-Operation-Reason', encodeUtf8HeaderValue(reason.trim()));
  }
  if (idempotencyKey) headers.set('X-Idempotency-Key', idempotencyKey);
  return { headers };
}
